import React, { useState, useEffect } from "react";

// --- CSS --- //
import "../../styles/gym-employee-dashboard.css";

// --- ASSETS --- //
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

// --- COMPONENTS --- //
import SearchBar from "../../components/SearchBar";
import EmployeeCard from "../../components/Cards/EmployeeCard";
// --> POPUPS
import { AddEditEmployeePopup, ActionConfirmationPopup, AdminVerificationPopup } from "../../utilities/popupUtils";

// --- FIREBASE --- //
import { addOrUpdateEmployee, getAllEmployees, getEmployeeData, deleteEmployees } from "../../utilities/firestore/firestoreUtils";

const GymEmployeeDashboard = () => {
    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [isFocused, setIsFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isAuthorized, setIsAuthorized] = useState(false); // Authorization status
    const [popupState, setPopupState] = useState({
        isAddEditPopupOpen: false,
        isConfirmationPopupOpen: false,
        isVerificationPopupOpen: true,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Usage
    const closePopup = (popupKey) => {
        setPopupState((prevState) => ({ ...prevState, [popupKey]: false }));
    };

    useEffect(() => {
        if (isAuthorized) handleFetchEmployeeData();

        if (!isAuthorized) {
            setPopupState((prevState) => ({
                ...prevState,
                isVerificationPopupOpen: true,
            }));
        }
    }, [isAuthorized]);

    // --- HELPER FUNCTIONS START --- //

    const handleFetchEmployeeData = async () => {
        setLoading(true);
        try {
            setSelectedEmployee(null);
            setSelectedEmployeeIds([]);
            try {
                const employeeNames = await getAllEmployees();
                let employeeDetails = await getEmployeeData(employeeNames);

                employeeDetails = employeeDetails.map((employee) => ({
                    ...employee,
                    id: employee.id,
                }));

                setEmployees(employeeDetails);
            } catch (error) {
            }
        } catch (error) {
            setError("Failed to fetch employee data.");
        } finally {
            setLoading(false);
        }
    };

    const showAddEmployeePopup = () => {
        setSelectedEmployee(null); // Reset selected employee for adding
        setPopupState((prevState) => ({
            ...prevState,
            isAddEditPopupOpen: true,
        }));
    };

    const showEditEmployeePopup = () => {
        const employeeToEdit = employees.find(
            (employee) => employee.id === selectedEmployeeIds[0]
        );
        setSelectedEmployee(employeeToEdit);
        setPopupState((prevState) => ({
            ...prevState,
            isAddEditPopupOpen: true,
        }));
    };

    const handleAddOrUpdateEmployee = async (employee) => {
        try {
            await addOrUpdateEmployee(employee);
            await handleFetchEmployeeData();
        } catch (error) {
        }
    };

    const deleteEmployee = () => {
        setPopupState((prevState) => ({
            ...prevState,
            isConfirmationPopupOpen: true,
        }));
    };

    const handleConfirmDelete = async () => {
        try {
            const selectedEmployeeNames = employees
                .filter((employee) => selectedEmployeeIds.includes(employee.id))
                .map((employee) => employee.id);

            await deleteEmployees(selectedEmployeeNames);
            await handleFetchEmployeeData();
            setPopupState((prevState) => ({
                ...prevState,
                isConfirmationPopupOpen: false,
            }));
        } catch (error) {
        }
    };

    const handleCancelDelete = () => {
        setPopupState((prevState) => ({
            ...prevState,
            isConfirmationPopupOpen: false,
        }));
    };

    const toggleSelection = (id) => {
        setSelectedEmployeeIds((prevSelected) =>
            prevSelected.includes(id)
                ? prevSelected.filter((employeeId) => employeeId !== id)
                : [...prevSelected, id]
        );
    };
    // --- HELPER FUNCTIONS END --- //



    // Filter employees based on the search query
    const filteredEmployees = employees.filter((employee) => {
        const query = searchQuery.toLowerCase();
        return (
            employee.name.toLowerCase().includes(query) ||
            employee.employeeCode.toLowerCase().includes(query)
        );
    });

    if (!isAuthorized) {
        return (
            <AdminVerificationPopup
                isVerificationOpen={popupState.isVerificationPopupOpen}
                onVerificationClose={() => closePopup('isVerificationPopupOpen')}
                setIsAuthorized={setIsAuthorized}
            />
        );
    }

    return (
        <div>
            <div className="employee-action-container">
                <div className="left-employee-action-container">
                    <SearchBar
                        isFocused={isFocused}
                        setIsFocused={setIsFocused}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        placeholder={"Search by name or PIN..."} />
                </div>
                <div className="right-employee-action-container">
                    <button
                        className="action-button"
                        onClick={showAddEmployeePopup}
                    >
                        <AddIcon />
                    </button>
                    {selectedEmployeeIds.length === 1 && (
                        <button
                            className="action-button"
                            onClick={showEditEmployeePopup}
                        >
                            <EditIcon />
                        </button>
                    )}
                    {selectedEmployeeIds.length >= 1 && (
                        <button
                            className="action-button"
                            onClick={deleteEmployee}
                        >
                            <DeleteIcon />
                        </button>
                    )}
                </div>
            </div>
            {loading ? (
                <div className="loading">Loading employees...</div>
            ) : error ? (
                <div className="error">{error}</div>
            ) : (
                <div className="employee-content-container">
                    {filteredEmployees.length > 0 ? (
                        filteredEmployees.map((employee) => (
                            <EmployeeCard
                                key={employee.id}
                                name={employee.name}
                                code={employee.employeeCode}
                                status={employee.status}
                                searchQuery={searchQuery}
                                isSelected={selectedEmployeeIds.includes(employee.id)}
                                onClick={() => toggleSelection(employee.id)}
                                showDetails={true}
                            />
                        ))
                    ) : (
                        <div className="no-results-container">
                            No results found for '{searchQuery}'
                        </div>
                    )}
                </div>
            )}
            <AddEditEmployeePopup
                isOpen={popupState.isAddEditPopupOpen}
                onClose={() => closePopup('isAddEditPopupOpen')}
                addOrUpdateEmployee={handleAddOrUpdateEmployee}
                selectedEmployee={selectedEmployee}
            />
            <ActionConfirmationPopup
                isOpen={popupState.isConfirmationPopupOpen}
                employees={employees}
                employeeIds={selectedEmployeeIds}
                message="Are you sure you want to delete the selected employees?"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default GymEmployeeDashboard;